import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Users, CheckCircle, Star, ArrowRight, Building2, GraduationCap, Shield, Heart, Target, Award, FileText, Euro, HandshakeIcon, Sparkles, Zap, Network, Globe, ChevronRight, Check, Crown } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_PLANS_API = `${BACKEND_URL}/api/public/frontoffice-plans`;

// V38.04 — Mapping nom d'icône (string en BDD) → composant React.
const ICON_MAP = {
  Sparkles, Users, Building2, HandshakeIcon, Shield, Star, Target, Award, FileText,
};

// V38.04 — Codes des plans affichés sur la page d'adhésion (les 4 collèges).
// L'offre COPPAM est servie sur sa propre page /offre-coppam.
const ADHESION_PLAN_CODES = new Set(['porteur', 'salarie', 'entreprise', 'partenaire']);

// Animation hook
const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
};

export default function AdhesionPage() {
  const [selectedType, setSelectedType] = useState(null);
  const [heroRef, heroInView] = useInView();
  const [typesRef, typesInView] = useInView();
  const [benefitsRef, benefitsInView] = useInView();

  // V38.04 — Fallback initial = données hardcodées (zéro régression si API KO).
  const defaultMemberTypes = [
    {
      id: 'porteur',
      title: 'Porteur de projet',
      icon: Sparkles,
      description: 'Vous avez un projet professionnel et souhaitez être accompagné',
      benefits: [
        'Accompagnement personnalisé au lancement',
        'Accès aux formations FORMA\'SCOP',
        'Réseau de partenaires et d\'experts',
        'Conseils juridiques et administratifs'
      ],
      cotisation: '50€',
      period: '/an',
      gradient: 'from-blue-500 to-cyan-500',
      bgGlow: 'bg-blue-500/20',
      popular: false
    },
    {
      id: 'salarie',
      title: 'Salarié',
      icon: Users,
      description: 'Vous êtes salarié et souhaitez bénéficier des avantages coopératifs',
      benefits: [
        'Formation continue et développement',
        'Participation aux assemblées générales',
        'Avantages coopératifs et mutualisés',
        'Accompagnement carrière'
      ],
      cotisation: '30€',
      period: '/an',
      gradient: 'from-green-500 to-emerald-500',
      bgGlow: 'bg-green-500/20',
      popular: false
    },
    {
      id: 'entreprise',
      title: 'Entreprise utilisatrice',
      icon: Building2,
      description: 'Votre entreprise souhaite adhérer au groupement d\'employeurs',
      benefits: [
        'Accès au personnel mutualisé O\'SCOP GE',
        'Flexibilité RH et optimisation des coûts',
        'Formations pour vos équipes',
        'Garanties solidaires COPPAM'
      ],
      cotisation: 'Sur mesure',
      period: '',
      gradient: 'from-purple-500 to-pink-500',
      bgGlow: 'bg-purple-500/20',
      popular: true
    },
    {
      id: 'partenaire',
      title: 'Partenaire institutionnel',
      icon: HandshakeIcon,
      description: 'Institution ou organisation souhaitant collaborer avec O\'SCOP',
      benefits: [
        'Participation à la gouvernance',
        'Co-construction de projets territoriaux',
        'Accès aux données et rapports',
        'Visibilité et communication partagée'
      ],
      cotisation: 'Convention',
      period: '',
      gradient: 'from-amber-500 to-orange-500',
      bgGlow: 'bg-amber-500/20',
      popular: false
    }
  ];

  // V38.04 — Plans dynamiques chargés depuis le Super Admin (fallback : defaults).
  const [memberTypes, setMemberTypes] = useState(defaultMemberTypes);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(PUBLIC_PLANS_API);
        const apiPlans = (res.data?.plans || [])
          .filter((p) => ADHESION_PLAN_CODES.has(p.code));
        if (apiPlans.length === 0 || cancelled) return;
        const mapped = apiPlans.map((p) => ({
          id: p.code,
          title: p.title,
          icon: ICON_MAP[p.icon] || Sparkles,
          description: p.description,
          benefits: p.benefits || [],
          cotisation: p.price_label || (p.price ? `${p.price}€` : ''),
          period: p.period || '',
          gradient: p.gradient || 'from-blue-500 to-cyan-500',
          bgGlow: 'bg-blue-500/20',
          popular: !!p.popular,
          cta_label: p.cta_label,
          cta_url: p.cta_url,
        }));
        setMemberTypes(mapped);
      } catch {
        // Silencieux : on garde les defaults (zéro régression front).
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const steps = [
    { num: 1, title: 'Choisir', desc: 'Sélectionnez votre collège', icon: Target },
    { num: 2, title: 'Compléter', desc: 'Remplissez le formulaire', icon: FileText },
    { num: 3, title: 'Valider', desc: 'Réglez votre cotisation', icon: CheckCircle },
    { num: 4, title: 'Bienvenue !', desc: 'Accédez aux services', icon: Star }
  ];

  const benefits = [
    { icon: GraduationCap, title: 'Formations', desc: 'Accès privilégié à FORMA\'SCOP', gradient: 'from-blue-500 to-cyan-500' },
    { icon: Shield, title: 'Garanties', desc: 'Couverture solidaire COPPAM', gradient: 'from-green-500 to-emerald-500' },
    { icon: Network, title: 'Réseau', desc: 'Communauté de professionnels', gradient: 'from-purple-500 to-pink-500' },
    { icon: FileText, title: 'Accompagnement', desc: 'Conseil et assistance', gradient: 'from-amber-500 to-orange-500' },
    { icon: Euro, title: 'Mutualisation', desc: 'Services à coût réduit', gradient: 'from-red-500 to-rose-500' },
    { icon: Award, title: 'Reconnaissance', desc: 'Label ESS et valorisation', gradient: 'from-indigo-500 to-purple-500' }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[70vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920&q=80" 
            alt="Partnership and membership" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-green-500/30 mb-6 px-4 py-2 font-semibold">
              <Heart className="w-4 h-4 mr-2" />
              Rejoignez la coopérative
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-green-100 to-white bg-clip-text text-transparent">
                Adhésion à O'SCOP
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mb-10 leading-relaxed">
              Intégrez un réseau coopératif solidaire dédié au développement économique 
              et social des <span className="text-amber-400 font-semibold">territoires ultramarins</span>.
            </p>

            <div className="flex flex-wrap gap-4">
              <Badge className="bg-slate-800/50 backdrop-blur-xl text-white border border-slate-700/50 px-4 py-2 font-semibold">
                <Users className="w-4 h-4 mr-2 text-blue-400" /> Multi-collèges
              </Badge>
              <Badge className="bg-slate-800/50 backdrop-blur-xl text-white border border-slate-700/50 px-4 py-2 font-semibold">
                <Shield className="w-4 h-4 mr-2 text-green-400" /> Gouvernance démocratique
              </Badge>
              <Badge className="bg-slate-800/50 backdrop-blur-xl text-white border border-slate-700/50 px-4 py-2 font-semibold">
                <Globe className="w-4 h-4 mr-2 text-amber-400" /> Impact territorial
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {steps.map((step, index) => (
              <React.Fragment key={step.num}>
                <div className="flex flex-col items-center text-center group">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white font-bold text-xl flex items-center justify-center mb-3 shadow-lg shadow-amber-500/25 group-hover:scale-110 transition-transform">
                    <step.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-white mb-1">{step.title}</h3>
                  <p className="text-sm text-slate-400">{step.desc}</p>
                </div>
                {index < steps.length - 1 && (
                  <ChevronRight className="w-6 h-6 text-slate-600 hidden md:block" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Member Types */}
      <section 
        ref={typesRef}
        className="py-24 bg-gradient-to-b from-slate-950 to-slate-900"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4 px-4 py-2 font-semibold">
              <Network className="w-4 h-4 mr-2" />
              Gouvernance partagée
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Choisissez votre <span className="text-purple-400">collège</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              O'SCOP rassemble différents types de membres dans une gouvernance démocratique
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {memberTypes.map((type, index) => (
              <div 
                key={type.id}
                className={`group relative bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 border transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl cursor-pointer ${
                  selectedType === type.id 
                    ? 'border-amber-500/50 shadow-2xl shadow-amber-500/10' 
                    : 'border-slate-700/50 hover:border-slate-600'
                } ${typesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onClick={() => setSelectedType(type.id)}
              >
                {/* Popular badge */}
                {type.popular && (
                  <div className="absolute -top-3 right-6">
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg shadow-amber-500/25 font-semibold">
                      <Crown className="w-3 h-3 mr-1" /> Populaire
                    </Badge>
                  </div>
                )}

                {/* Glow effect */}
                <div className={`absolute -inset-0.5 ${type.bgGlow} rounded-3xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500`} />
                
                <div className="relative">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${type.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <type.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-black text-white">{type.cotisation}</span>
                      <span className="text-slate-400">{type.period}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">{type.title}</h3>
                  <p className="text-slate-400 mb-6">{type.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {type.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-slate-300">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${type.gradient} flex items-center justify-center flex-shrink-0`}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full font-bold py-6 transition-all duration-300 ${
                      selectedType === type.id
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25'
                        : 'bg-slate-700/50 hover:bg-slate-700 text-white'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = `/register?type=${type.id}`;
                    }}
                  >
                    {selectedType === type.id ? 'Adhérer maintenant' : 'Sélectionner'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        ref={benefitsRef}
        className="py-24 bg-slate-900 relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-green-600/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-green-500/20 text-green-300 border border-green-500/30 mb-4 px-4 py-2 font-semibold">
              <Zap className="w-4 h-4 mr-2" />
              Avantages membres
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Les avantages de <span className="text-green-400">l'adhésion</span>
            </h2>
            <p className="text-lg text-slate-400">En rejoignant O'SCOP, vous accédez à un ensemble de services mutualisés</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((item, index) => (
              <div 
                key={index} 
                className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 text-center ${benefitsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-green-600 via-emerald-600 to-green-700 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Prêt à rejoindre <span className="text-green-200">O'SCOP</span> ?
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Faites partie d'un mouvement coopératif innovant pour les Outre-mer.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-green-600 hover:bg-slate-100 font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105"
              onClick={() => window.location.href = '/register'}
            >
              Commencer l'adhésion
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg transition-all duration-300"
              onClick={() => window.location.href = '/contact'}
            >
              Des questions ? Contactez-nous
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
